/**
 * Dropzone — drag-and-drop / click-to-browse file picker.
 * Presentational: validation + upload live in the caller's `onFile` handler.
 */
import { FileText } from 'lucide-react';
import { type DragEvent, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface DropzoneProps {
  onFile: (file: File) => void;
  /** Comma-separated accept list, also shown in the hint line. */
  accept?: string;
  /** Size hint shown after the accept list. */
  maxLabel?: string;
  disabled?: boolean;
  className?: string;
}

export function Dropzone({
  onFile,
  accept = '.txt,.zip',
  maxLabel = 'up to 100 MB',
  disabled,
  className,
}: DropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  function handleDrop(e: DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const f = e.dataTransfer.files[0];
    if (f) onFile(f);
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: drag-drop zone needs onDragOver/onDrop; the actionable control is the Browse button
    <div
      className={cn(
        'flex flex-col items-center gap-2 rounded-xl border-[1.5px] border-dashed p-8 text-center transition-colors',
        dragOver ? 'border-primary' : 'border-[#d8d8dc]',
        disabled && 'pointer-events-none opacity-60',
        className,
      )}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      <FileText className="h-[26px] w-[26px] text-faint" />
      <p className="text-[15px] font-semibold">Drop your export here</p>
      <p className="text-[13px] text-faint">
        {accept} · {maxLabel}
      </p>
      <Button
        variant="outline"
        size="sm"
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        Browse files
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        disabled={disabled}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          // Reset so selecting the same file again still fires `change`.
          e.target.value = '';
        }}
      />
    </div>
  );
}
